// Adaptive outbound mic gate.
// The open threshold sits a fixed margin above the recent room-noise floor,
// so a hot mic and a quiet mic do not share one absolute level.

export const MIC_GATE_OFFSET_STORAGE_KEY = 'bp_mic_gate_offset_db';
export const MIC_GATE_OFFSET_DEFAULT = 0;
export const MIC_GATE_OFFSET_MIN = -6;
export const MIC_GATE_OFFSET_MAX = 6;

export const MIC_GATE_OPEN_MARGIN_DB = 11;
export const MIC_GATE_CLOSE_MARGIN_DB = 5;
export const MIC_GATE_HANG_MS = 250;
export const MIC_GATE_OPEN_HOLD_MS = 80;
export const MIC_GATE_SEED_MS = 500;
export const MIC_GATE_WINDOW_MS = 1200;
export const MIC_GATE_CLOSED_BEFORE_RISE_MS = 300;
export const MIC_GATE_FLOOR_MIN_DB = -62;
export const MIC_GATE_FLOOR_MAX_DB = -12;
export const MIC_GATE_FALL_TC_MS = 250;
export const MIC_GATE_STEADY_SPREAD_DB = 6;
export const MIC_GATE_SPEECH_SPREAD_DB = 10;
export const MIC_GATE_STEADY_RISE_DB_PER_SEC = 30;
export const MIC_GATE_SLOW_RISE_DB_PER_SEC = 8;
export const MIC_GATE_METER_SPAN_DB = 24;
export const MIC_GATE_LEVEL_MIN_DB = -80;
export const MIC_GATE_MAX_STEP_MS = 250;

export const SPEECH_HIGHPASS_HZ = 200;
export const SPEECH_LOWPASS_HZ = 3500;

// Remote talking glow still uses the pre-gate byte-frequency average.
export const REMOTE_TALKING_LEVEL = 12;

export function clampMicGateOffset(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return MIC_GATE_OFFSET_DEFAULT;
  return Math.min(MIC_GATE_OFFSET_MAX, Math.max(MIC_GATE_OFFSET_MIN, n));
}

export function readStoredMicGateOffset(storage) {
  const store = storage || (typeof localStorage !== 'undefined' ? localStorage : null);
  if (!store) return MIC_GATE_OFFSET_DEFAULT;
  try {
    const raw = store.getItem(MIC_GATE_OFFSET_STORAGE_KEY);
    if (raw == null) return MIC_GATE_OFFSET_DEFAULT;
    return clampMicGateOffset(Math.round(Number(raw)));
  } catch (_) {
    return MIC_GATE_OFFSET_DEFAULT;
  }
}

export function formatGateOffset(offsetDb) {
  const n = Math.round(clampMicGateOffset(offsetDb));
  if (n > 0) return `+${n} dB`;
  return `${n} dB`;
}

export function micGateMargins(offsetDb) {
  const offset = clampMicGateOffset(offsetDb);
  const openMargin = Math.max(6, MIC_GATE_OPEN_MARGIN_DB + offset);
  const closeMargin = Math.min(
    openMargin - 4,
    Math.max(2, MIC_GATE_CLOSE_MARGIN_DB + offset)
  );
  return { offset, openMargin, closeMargin };
}

export function levelDbFromSamples(samples) {
  if (!samples || !samples.length) return MIC_GATE_LEVEL_MIN_DB;
  let sum = 0;
  for (let i = 0; i < samples.length; i += 1) {
    const s = Number(samples[i]);
    if (!Number.isFinite(s)) continue;
    sum += s * s;
  }
  const rms = Math.sqrt(sum / samples.length);
  if (!(rms > 1e-8)) return MIC_GATE_LEVEL_MIN_DB;
  const db = 20 * Math.log10(rms);
  if (!Number.isFinite(db)) return MIC_GATE_LEVEL_MIN_DB;
  return Math.max(MIC_GATE_LEVEL_MIN_DB, Math.min(0, db));
}

export function micGateMeterView(meter, offsetDb) {
  if (!meter || !meter.measured) {
    const { openMargin } = micGateMargins(offsetDb);
    return {
      levelPercent: 0,
      openPercent: (openMargin / MIC_GATE_METER_SPAN_DB) * 100
    };
  }
  return micGateMeterPositions(meter.levelDb, meter.noiseFloorDb, meter.openThresholdDb);
}

export function micGateMeterPositions(levelDb, floorDb, openThresholdDb, spanDb = MIC_GATE_METER_SPAN_DB) {
  const span = spanDb > 0 ? spanDb : MIC_GATE_METER_SPAN_DB;
  const pos = (db) => {
    const n = Number(db);
    if (!Number.isFinite(n) || !Number.isFinite(floorDb)) return 0;
    return Math.min(100, Math.max(0, ((n - floorDb) / span) * 100));
  };
  return {
    levelPercent: pos(levelDb),
    openPercent: pos(openThresholdDb)
  };
}

function clampFloor(db) {
  return Math.min(MIC_GATE_FLOOR_MAX_DB, Math.max(MIC_GATE_FLOOR_MIN_DB, db));
}

function clampLevel(db) {
  const n = Number(db);
  if (!Number.isFinite(n)) return MIC_GATE_LEVEL_MIN_DB;
  return Math.max(MIC_GATE_LEVEL_MIN_DB, Math.min(0, n));
}

export function createMicGateState() {
  return {
    noiseFloorDb: null,
    gateOpen: false,
    closedForMs: 0,
    aboveOpenForMs: 0,
    hangRemainingMs: 0,
    seedRemainingMs: MIC_GATE_SEED_MS,
    windowMs: 0,
    samples: []
  };
}

function pushSample(state, level, dt) {
  state.samples.push({ db: level, dt });
  state.windowMs += dt;
  while (
    state.samples.length > 1 &&
    state.windowMs - state.samples[0].dt >= MIC_GATE_WINDOW_MS
  ) {
    state.windowMs -= state.samples.shift().dt;
  }
}

function windowSpread(samples) {
  let min = Infinity;
  let max = -Infinity;
  for (let i = 0; i < samples.length; i += 1) {
    const db = samples[i].db;
    if (db < min) min = db;
    if (db > max) max = db;
  }
  return { min, max, spread: max - min };
}

function fastFall(state, level, dt) {
  if (!(level < state.noiseFloorDb) || !(dt > 0)) return;
  const alpha = 1 - Math.exp(-dt / MIC_GATE_FALL_TC_MS);
  state.noiseFloorDb = clampFloor(state.noiseFloorDb + (level - state.noiseFloorDb) * alpha);
}

function riseFloor(state, dt) {
  if (state.windowMs < MIC_GATE_WINDOW_MS || !state.samples.length) return;
  const { min, spread } = windowSpread(state.samples);
  const target = clampFloor(min);
  if (!(target > state.noiseFloorDb + 0.05)) return;

  if (spread < MIC_GATE_STEADY_SPREAD_DB) {
    const step = MIC_GATE_STEADY_RISE_DB_PER_SEC * (dt / 1000);
    state.noiseFloorDb = clampFloor(Math.min(target, state.noiseFloorDb + step));
    return;
  }

  const speechLike = spread >= MIC_GATE_SPEECH_SPREAD_DB;
  if (speechLike && (state.gateOpen || state.closedForMs < MIC_GATE_CLOSED_BEFORE_RISE_MS)) {
    return;
  }
  const step = MIC_GATE_SLOW_RISE_DB_PER_SEC * (dt / 1000);
  state.noiseFloorDb = clampFloor(Math.min(target, state.noiseFloorDb + step));
}

function snapshot(state, offsetDb, open) {
  const { openMargin, closeMargin, offset } = micGateMargins(offsetDb);
  const floor = state.noiseFloorDb;
  return {
    open: !!open,
    noiseFloorDb: floor,
    openThresholdDb: floor + openMargin,
    closeThresholdDb: floor + closeMargin,
    offsetDb: offset
  };
}

export function stepMicGate(state, levelDb, dtMs, options = {}) {
  const offsetDb = options.offsetDb;
  const forceClosed = !!options.forceClosed;
  const level = clampLevel(levelDb);
  const dt = Math.min(MIC_GATE_MAX_STEP_MS, Number(dtMs) > 0 ? Number(dtMs) : 0);

  if (state.noiseFloorDb == null || !Number.isFinite(state.noiseFloorDb)) {
    state.noiseFloorDb = clampFloor(level);
  }

  if (!(dt > 0)) {
    if (forceClosed) {
      state.gateOpen = false;
      state.hangRemainingMs = 0;
      state.aboveOpenForMs = 0;
      state.closedForMs = 0;
    }
    return snapshot(state, offsetDb, state.gateOpen);
  }

  if (forceClosed) {
    fastFall(state, level, dt);
    state.gateOpen = false;
    state.hangRemainingMs = 0;
    state.aboveOpenForMs = 0;
    state.closedForMs = 0;
    return snapshot(state, offsetDb, false);
  }

  pushSample(state, level, dt);
  fastFall(state, level, dt);

  if (state.seedRemainingMs > 0) {
    state.seedRemainingMs = Math.max(0, state.seedRemainingMs - dt);
    state.gateOpen = false;
    state.hangRemainingMs = 0;
    state.aboveOpenForMs = 0;
    state.closedForMs += dt;
    return snapshot(state, offsetDb, false);
  }

  riseFloor(state, dt);

  const { openMargin, closeMargin } = micGateMargins(offsetDb);
  const openAt = state.noiseFloorDb + openMargin;
  const closeAt = state.noiseFloorDb + closeMargin;

  if (state.gateOpen) {
    state.closedForMs = 0;
    state.aboveOpenForMs = 0;
    if (level >= closeAt) {
      state.hangRemainingMs = MIC_GATE_HANG_MS;
    } else {
      state.hangRemainingMs -= dt;
      if (state.hangRemainingMs <= 0) {
        state.gateOpen = false;
        state.hangRemainingMs = 0;
        state.closedForMs = 0;
      }
    }
  } else {
    state.closedForMs += dt;
    if (level > openAt) {
      state.aboveOpenForMs += dt;
      if (state.aboveOpenForMs >= MIC_GATE_OPEN_HOLD_MS) {
        state.gateOpen = true;
        state.hangRemainingMs = MIC_GATE_HANG_MS;
        state.closedForMs = 0;
      }
    } else {
      state.aboveOpenForMs = 0;
    }
  }

  return snapshot(state, offsetDb, state.gateOpen);
}
